import { createApi } from "@reduxjs/toolkit/query/react";
import { isSameSecond } from "date-fns";

import { axiosBaseQuery } from "@/api/base";
import {
  PromptLibraryItem,
  CreatePromptLibraryItemPayload,
  UpdatePromptLibraryItemPayload,
  GetPromptLibraryItemPayload,
} from "./promptLibrary.types";

const TAG = "promptLibrary";

const wasPromptLibraryItemEdited = (item: PromptLibraryItem) => !isSameSecond(item.created_at, item.updated_at);

export const promptLibraryApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: "/pf-document/" }),
  reducerPath: "promptLibrary",
  tagTypes: [TAG],
  endpoints: builder => ({
    getPromptLibraryItems: builder.query<
      Array<PromptLibraryItem & { wasEdited: boolean }>,
      GetPromptLibraryItemPayload
    >({
      query: payload => ({
        url: "prompt-library-item",
        method: "GET",
        params: payload,
      }),
      transformResponse: (response: Array<PromptLibraryItem>) =>
        response.map(item => ({
          ...item,
          wasEdited: wasPromptLibraryItemEdited(item),
        })),
    }),
    getSuggestedPromptLibraryItemName: builder.query<string, string | undefined>({
      query: prompt => ({
        url: "prompt-library-item-suggested-name",
        method: "GET",
        params: { prompt },
      }),
    }),
    createPromptLibraryItems: builder.mutation<PromptLibraryItem, CreatePromptLibraryItemPayload>({
      query: payload => ({
        url: "prompt-library-item",
        method: "post",
        data: payload,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        const { data } = await queryFulfilled;

        const rootState = getState();
        const args = promptLibraryApi.util.selectCachedArgsForQuery(rootState, "getPromptLibraryItems");
        for (const arg of args) {
          dispatch(
            promptLibraryApi.util.updateQueryData("getPromptLibraryItems", arg, items => [
              { ...data, wasEdited: false },
              ...items,
            ]),
          );
        }
      },
    }),
    updatePromptLibraryItems: builder.mutation<PromptLibraryItem, UpdatePromptLibraryItemPayload>({
      query: payload => ({
        url: `prompt-library-item`,
        method: "put",
        data: payload,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        const { data } = await queryFulfilled;

        const rootState = getState();
        const args = promptLibraryApi.util.selectCachedArgsForQuery(rootState, "getPromptLibraryItems");
        for (const arg of args) {
          dispatch(
            promptLibraryApi.util.updateQueryData("getPromptLibraryItems", arg, items => {
              const index = items.findIndex(item => item.id === data.id);
              if (index !== -1) {
                items[index] = { ...data, wasEdited: wasPromptLibraryItemEdited(data) };
              }
            }),
          );
        }
      },
    }),
    togglePromptLibraryItemFavorite: builder.mutation<void, string>({
      query: id => ({
        url: `prompt-library-item/toggle-favorite/${id}`,
        method: "put",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;

        const rootState = getState();
        const args = promptLibraryApi.util.selectCachedArgsForQuery(rootState, "getPromptLibraryItems");

        for (const arg of args) {
          dispatch(
            promptLibraryApi.util.updateQueryData("getPromptLibraryItems", arg, items => {
              const index = items.findIndex(item => item.id === id);
              if (index !== -1 && items[index]) {
                items[index].is_favorite = !items[index].is_favorite;
              }
            }),
          );
        }
      },
    }),
    deletePromptLibraryItems: builder.mutation<void, string>({
      query: id => ({
        url: "prompt-library-item/" + id,
        method: "delete",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        await queryFulfilled;

        const rootState = getState();
        const args = promptLibraryApi.util.selectCachedArgsForQuery(rootState, "getPromptLibraryItems");

        for (const arg of args) {
          dispatch(
            promptLibraryApi.util.updateQueryData("getPromptLibraryItems", arg, items =>
              items.filter(item => item.id !== id),
            ),
          );
        }
      },
    }),
  }),
});

export const {
  useGetPromptLibraryItemsQuery,
  useGetSuggestedPromptLibraryItemNameQuery,
  useCreatePromptLibraryItemsMutation,
  useUpdatePromptLibraryItemsMutation,
  useDeletePromptLibraryItemsMutation,
  useTogglePromptLibraryItemFavoriteMutation,
} = promptLibraryApi;
